import { CardInstance } from './card.service';
import { FieldInstance } from './field.service';
import { CoreCardReviewRendererType, CoreCustomCardType } from './workflow.type';

/**
 * An extension helper to Workflow card class
 * By default it process only visible cards and fields
 */
export class CardHelper {
  constructor(private readonly card: CardInstance) {}

  /**
   * return list of all visible fields in the card including nested fields
   */
  public get flatVisibleFields(): FieldInstance[] {
    if (!this.card.visible) {
      return [];
    }
    return this.flattenVisibleFields(this.card.fields).filter((f) => f.visible);
  }

  /**
   * return list of invalid fields in the card, by default it filters visible fields
   */
  public get invalidFields(): FieldInstance[] {
    return this.flatVisibleFields.filter((f) => !f.valid);
  }

  /**
   * Indicate if the card is complete
   */
  public get isCardComplete(): boolean {
    return this.isCardValid;
  }

  /**
   * Indicate if the card is touched
   */
  public get isCardTouched(): boolean {
    return this.touchedFields.length > 0;
  }

  /**
   * Indicate if the card is valid
   */
  public get isCardValid(): boolean {
    return this.invalidFields.length === 0 && this.card.valid;
  }

  /**
   * return list of fields that are touched in the card, by default it filters visible fields
   */
  public get touchedFields(): FieldInstance[] {
    return this.flatVisibleFields.filter((f) => f.dirty);
  }

  public get customCardType(): CoreCustomCardType {
    return this.card.config.customCardType || 'standard';
  }

  public get reviewRendererType(): CoreCardReviewRendererType {
    const defaultRendererType: CoreCardReviewRendererType = 'standard-card-review-renderer';
    return this.card.config.reviewRendererType || defaultRendererType;
  }

  private flattenVisibleFields(fields: FieldInstance[]): FieldInstance[] {
    let flatFields = [];
    const visibleFields = fields.filter((f) => f.visible);
    for (const field of visibleFields) {
      if (!field.visible) {
        continue;
      }

      flatFields.push(field);
      if (field.nestedFields.length > 0) {
        flatFields = [...flatFields, ...this.flattenVisibleFields(field.nestedFields)];
      }
    }
    return flatFields;
  }
}
